import React, {useCallback, useEffect, useMemo, useState} from "react";
import {MenuItem, Tab, Tabs, TextField, Tooltip, SvgIcon} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {Link, useParams} from "react-router-dom";
import {Col, Row} from "reactstrap";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import SummaryActionCard from "../../../../../../components/app-specific/summary-cards/summary-action-card";
import OrderInformationViewReadOnlyAddressesSection
    from "../../../../../../components/app-specific/order-inforamtion-shared-sections/read-only-addresses";
import IconButton from "../../../../../../components/base/button/icon_button";
import UpdatePurchaseOrderPropertiesDialog from "../../../../../../components/dialogs/update-purchase-order-properties";
import PurchaseVendorInfoCard from "../../shared/vendor-info-card";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useAppSelector} from "../../../../../../redux";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useQuery from "../../../../../../hooks/use-query";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import DateUtils from "../../../../../../../core/services/date-utils";
import Utils from "../../../../../../../core/services/utils/utils";
import {PurchaseOrderStatusList} from "../../constants";
import {buildMockPurchaseOrder, recomputePurchaseTotals} from "../../shared/mock-documents";
import PurchaseOrderSingleProductsSection from "./sections/products";
import PurchaseOrderSingleLogsSection from "./sections/logs";
import {ReactComponent as Printer} from "../../../../../../../assets/images/printer.svg";

const tabs = Object.freeze({
    products: 'products',
    addresses: 'addresses',
    logs: 'logs',
});

const documentBodyIds = {
    vendor: 'vendor',
    vendorPO: 'vendorPO',
    reference: 'reference',
    date: 'date',
    requiredDate: 'requiredDate',
    warehouse: 'warehouse',
    currency: 'currency',
};

const billBodyIds = {
    status: 'status',
    subtotal: 'subtotal',
    discount: 'discount',
    tax: 'tax',
    total: 'total',
    balance: 'balance',
};

const initUpdatePurchaseOrderPropertiesDialog = {
    open: false,
    entityToShow: 'warehouse',
    warehouse: null,
};

/**
 * Formats a server date for summary display.
 */
const formatSummaryDate = (value) => {
    if (!value) {
        return '--';
    }
    return DateUtils.serverToLocal(value).format(DateUtils.dateFormat);
};

/**
 * Formats warehouse entity (or legacy string) for summary display.
 */
const formatWarehouseLabel = (warehouse) => {
    if (!warehouse) {
        return '--';
    }
    if (typeof warehouse === 'string') {
        return warehouse;
    }
    if (warehouse.name && warehouse.code) {
        return `${warehouse.name} (${warehouse.code})`;
    }
    return warehouse.code ?? warehouse.name ?? '--';
};

/**
 * Interactive vendor purchase order single — products / addresses / logs.
 */
const PurchaseOrderSingleView = () => {
    const {vendorId, orderId} = useParams();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.orderSingle'});
    const defaultVendor = useAppSelector((state) => state.vendorInformation);
    const operatorAccessLevels = useAccessLevels();
    const canManageOrderDetails = operatorAccessLevels.packages.purchasing.managePurchaseOrderDetails;
    const isMounted = useIsMounted();
    const [query, setQuery] = useQuery();
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [order, setOrder] = useState(null);
    const [propertiesDialog, setPropertiesDialog] = useState(initUpdatePurchaseOrderPropertiesDialog);
    const {configureNotebookTags} = useConfigureNotebook('vendor-purchase-order-single');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const tab = useMemo(() => {
        const requested = query?.tab;
        if (requested && Object.values(tabs).includes(requested)) {
            return requested;
        }
        return tabs.products;
    }, [query?.tab]);

    const tabsList = useMemo(() => ([
        {value: tabs.products, label: t('tabs.products')},
        {value: tabs.addresses, label: t('tabs.addresses')},
        {value: tabs.logs, label: t('tabs.logs')},
    ]), [t]);

    useEffect(() => {
        if (!defaultVendor?.code) {
            return;
        }
        configureNotebookTags((tags) => tags.vendorPurchaseOrderSingle(defaultVendor.code, orderId));
        configureBehavioralTool((tags) => tags.vendorPurchaseOrderSingle(defaultVendor.code, orderId));
    }, [configureBehavioralTool, configureNotebookTags, defaultVendor?.code, orderId]);

    /**
     * Loads purchase order shell data.
     */
    const loadOrder = useCallback(async () => {
        setLoading(true);
        await Utils.wait(300);
        if (!isMounted()) {
            return;
        }
        setOrder(buildMockPurchaseOrder({
            orderId,
            vendorId,
            vendorName: defaultVendor?.name,
            vendorCode: defaultVendor?.code,
        }));
        setLoading(false);
    }, [defaultVendor?.code, defaultVendor?.name, isMounted, orderId, vendorId]);

    useEffect(() => {
        void loadOrder();
    }, [loadOrder]);

    /**
     * Syncs the active tab into the URL query string.
     */
    const onTabChange = useCallback((_, value) => {
        setQuery({tab: value}, {replace: true});
    }, [setQuery]);

    /**
     * Appends a shell activity log entry.
     */
    const pushLog = useCallback((action, details) => ({
        id: `log-${Date.now()}`,
        loggedAt: new Date().toISOString(),
        user: 'current.user',
        action,
        details,
    }), []);

    /**
     * Applies a mutator to the order document and prepends an optional log.
     * // TODO: replace with response.data from live APIs
     */
    const applyOrderMutation = useCallback((mutator, log) => {
        setOrder((prev) => {
            if (!prev) {
                return prev;
            }
            const next = mutator(prev);
            if (!next) {
                return prev;
            }
            if (!log) {
                return next;
            }
            return {
                ...next,
                logs: [log, ...(next.logs ?? [])],
            };
        });
        return true;
    }, []);

    /**
     * SummaryInfoCard field save (vendorPO, reference) or warehouse dialog.
     */
    const editDocumentField = useCallback(async (entry, value) => {
        if (entry.id === documentBodyIds.warehouse) {
            let abort = false;
            let nextWarehouse;
            [nextWarehouse, abort] = await new Promise((resolve) => {
                setPropertiesDialog({
                    open: true,
                    entityToShow: 'warehouse',
                    warehouse: order?.warehouse ?? null,
                    close: () => {
                        resolve([undefined, true]);
                        setPropertiesDialog(initUpdatePurchaseOrderPropertiesDialog);
                    },
                    update: (values) => {
                        resolve([values.warehouse, false]);
                    },
                });
            });
            if (abort) {
                return true;
            }
            // TODO: live UpdatePurchaseOrderProperties API → setOrder(response.data)
            await Utils.wait(150);
            if (!isMounted()) {
                return false;
            }
            return applyOrderMutation(
                (prev) => ({...prev, warehouse: nextWarehouse}),
                pushLog(
                    t('logs.actions.fieldUpdated'),
                    t('logs.fieldUpdatedDetails', {
                        field: entry.title,
                        value: formatWarehouseLabel(nextWarehouse),
                    }),
                ),
            );
        }

        const nextValue = value?.toString?.() ?? value;
        // TODO: live UpdatePurchaseOrder field API
        await Utils.wait(150);
        if (!isMounted()) {
            return false;
        }
        return applyOrderMutation(
            (prev) => ({...prev, [entry.id]: nextValue}),
            pushLog(
                t('logs.actions.fieldUpdated'),
                t('logs.fieldUpdatedDetails', {
                    field: entry.title,
                    value: nextValue || '--',
                }),
            ),
        );
    }, [applyOrderMutation, isMounted, order?.warehouse, pushLog, t]);

    /**
     * Updates document status from the bill-info action card.
     */
    const onStatusChange = useCallback(async (event) => {
        const status = event.target.value;
        // TODO: live UpdatePurchaseOrderStatus API
        await Utils.wait(150);
        if (!isMounted()) {
            return;
        }
        applyOrderMutation(
            (prev) => ({...prev, status}),
            pushLog(t('logs.actions.statusChanged'), t('logs.statusChangedDetails', {status})),
        );
    }, [applyOrderMutation, isMounted, pushLog, t]);

    /**
     * Shell API: patch order qty (> 0); trims surplus serials; refreshes order.
     */
    const onUpdateOrderQty = useCallback(async (item, newValue) => {
        setUpdating(true);
        // TODO: live UpdatePurchaseOrderItemQty API → setOrder(response.data)
        await Utils.wait(200);
        if (!isMounted()) {
            return false;
        }
        let qty = Number(newValue);
        if (Number.isNaN(qty) || qty <= 0) {
            setUpdating(false);
            return false;
        }
        qty = Math.trunc(qty);
        const resultFlag = applyOrderMutation((prev) => {
            const items = (prev.items ?? []).map((row) => {
                if (row.id !== item.id) {
                    return row;
                }
                const serials = (row.serials ?? []).slice(0, qty);
                const cost = Number(row.cost) || 0;
                return {
                    ...row,
                    orderQty: qty,
                    serials,
                    extendedCost: qty * cost,
                };
            });
            return {
                ...prev,
                items,
                ...recomputePurchaseTotals(items, prev),
            };
        }, pushLog(
            t('logs.actions.fieldUpdated'),
            t('logs.fieldUpdatedDetails', {
                field: `${item.partNo} ${t('products.columns.orderQty')}`,
                value: qty,
            }),
        ));
        setUpdating(false);
        return resultFlag;
    }, [applyOrderMutation, isMounted, pushLog, t]);

    /**
     * Shell API: patch unit cost; refreshes order totals from backend-shaped response.
     */
    const onUpdateCost = useCallback(async (item, newValue) => {
        setUpdating(true);
        // TODO: live UpdatePurchaseOrderItemCost API → setOrder(response.data)
        await Utils.wait(200);
        if (!isMounted()) {
            return false;
        }
        let cost = Number(newValue);
        if (Number.isNaN(cost) || cost < 0) {
            cost = 0;
        }
        const resultFlag = applyOrderMutation((prev) => {
            const items = (prev.items ?? []).map((row) => {
                if (row.id !== item.id) {
                    return row;
                }
                const qty = Number(row.orderQty) || 0;
                return {
                    ...row,
                    cost,
                    extendedCost: qty * cost,
                };
            });
            return {
                ...prev,
                items,
                ...recomputePurchaseTotals(items, prev),
            };
        }, pushLog(
            t('logs.actions.fieldUpdated'),
            t('logs.fieldUpdatedDetails', {
                field: `${item.partNo} ${t('products.columns.cost')}`,
                value: formatMoney(cost),
            }),
        ));
        setUpdating(false);
        return resultFlag;
    }, [applyOrderMutation, isMounted, pushLog, t]);

    /**
     * Shell API: add a serial number for a line; replaces order with fetched state.
     */
    const onAddSerial = useCallback(async (item, serial) => {
        setUpdating(true);
        // TODO: live AddPurchaseOrderItemSerial API → setOrder(response.data)
        await Utils.wait(200);
        if (!isMounted()) {
            return false;
        }
        const resultFlag = applyOrderMutation((prev) => {
            const items = (prev.items ?? []).map((row) => {
                if (row.id !== item.id) {
                    return row;
                }
                const max = Number(row.orderQty) || 0;
                const current = row.serials ?? [];
                if (current.includes(serial) || current.length >= max) {
                    return row;
                }
                return {...row, serials: [...current, serial]};
            });
            return {...prev, items};
        }, pushLog(
            t('logs.actions.serialAdded'),
            t('logs.serialAddedDetails', {partNo: item.partNo, serial}),
        ));
        setUpdating(false);
        return resultFlag;
    }, [applyOrderMutation, isMounted, pushLog, t]);

    /**
     * Shell API: remove a serial number for a line; replaces order with fetched state.
     */
    const onRemoveSerial = useCallback(async (item, serial) => {
        setUpdating(true);
        // TODO: live RemovePurchaseOrderItemSerial API → setOrder(response.data)
        await Utils.wait(200);
        if (!isMounted()) {
            return false;
        }
        const resultFlag = applyOrderMutation((prev) => {
            const items = (prev.items ?? []).map((row) => {
                if (row.id !== item.id) {
                    return row;
                }
                return {
                    ...row,
                    serials: (row.serials ?? []).filter((value) => value !== serial),
                };
            });
            return {...prev, items};
        }, pushLog(
            t('logs.actions.serialRemoved'),
            t('logs.serialRemovedDetails', {partNo: item.partNo, serial}),
        ));
        setUpdating(false);
        return resultFlag;
    }, [applyOrderMutation, isMounted, pushLog, t]);

    const documentInfoSummary = useMemo(() => ({
        header: {
            title: t('summary.orderNo'),
            value: order?.orderNo ?? orderId,
            loadingValue: t('loading'),
            copyable: true,
        },
        body: [
            {
                id: documentBodyIds.vendor,
                title: t('summary.vendor'),
                value: order?.vendorName ?? '--',
            },
            {
                id: documentBodyIds.vendorPO,
                title: t('summary.vendorPO'),
                value: order?.vendorPO ?? '',
                editable: canManageOrderDetails && !loading && !!order,
                clearable: true,
                edit: editDocumentField,
            },
            {
                id: documentBodyIds.reference,
                title: t('summary.reference'),
                value: order?.reference ?? '',
                editable: canManageOrderDetails && !loading && !!order,
                clearable: true,
                edit: editDocumentField,
            },
            {
                id: documentBodyIds.date,
                title: t('summary.date'),
                value: formatSummaryDate(order?.date),
            },
            {
                id: documentBodyIds.requiredDate,
                title: t('summary.requiredDate'),
                value: formatSummaryDate(order?.requiredDate),
            },
            {
                id: documentBodyIds.warehouse,
                title: t('summary.warehouse'),
                value: formatWarehouseLabel(order?.warehouse),
                editable: canManageOrderDetails && !loading && !!order,
                edit: editDocumentField,
                openDialog: true,
            },
            {
                id: documentBodyIds.currency,
                title: t('summary.currency'),
                value: order?.currency ?? '--',
            },
        ],
    }), [canManageOrderDetails, editDocumentField, loading, order, orderId, t]);

    const billInfoSummary = useMemo(() => ({
        header: {
            title: t('billInfo.status'),
            value: order?.status ?? '--',
            loadingValue: t('loading'),
        },
        body: [
            {
                id: billBodyIds.status,
                title: t('billInfo.status'),
                freeFormValue: true,
                value: (
                    <TextField
                        select
                        size={'small'}
                        fullWidth
                        value={order?.status ?? ''}
                        disabled={loading || !order || updating || !canManageOrderDetails}
                        onChange={onStatusChange}
                    >
                        {
                            PurchaseOrderStatusList.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                ),
            },
            {
                id: billBodyIds.subtotal,
                title: t('billInfo.subtotal'),
                value: order ? formatMoney(order.subtotal) : '--',
            },
            {
                id: billBodyIds.discount,
                title: t('billInfo.discount'),
                value: order ? formatMoney(order.discount) : '--',
            },
            {
                id: billBodyIds.tax,
                title: t('billInfo.tax'),
                value: order ? formatMoney(order.tax) : '--',
            },
            {
                id: billBodyIds.total,
                title: t('billInfo.total'),
                value: order ? formatMoney(order.total) : '--',
            },
            {
                id: billBodyIds.balance,
                title: t('billInfo.balance'),
                value: order ? formatMoney(order.balance) : '--',
            },
        ],
    }), [canManageOrderDetails, loading, onStatusChange, order, t, updating]);

    return (
        <div className={'purchase-order-single-view w-100'}>
            <Row className={'no-gutters w-100 align-items-center justify-content-between'}>
                <Row className={'no-gutters align-items-center'}>
                    <IconButton
                        component={Link}
                        to={routeFunctions.vendor.purchases.orders.base(vendorId)}
                        replace
                        color={'paper.secondary'}
                        aria-label={t('actions.back')}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <p className={'text-lg font-weight-semi-bold ml-2'}>
                        {
                            loading
                                ? t('titleLoading')
                                : t('title', {orderNo: order?.orderNo ?? orderId})
                        }
                    </p>
                </Row>
                {
                    operatorAccessLevels.packages.bizPrint.printPurchaseOrder &&
                    <Tooltip arrow title={t('actions.print')}>
                        <IconButton
                            color={'paper.secondary'}
                            component={Link}
                            target={'_blank'}
                            to={routeFunctions.print.purchaseOrder(vendorId, orderId)}
                            aria-label={t('actions.print')}
                        >
                            <SvgIcon className={'icon large'} component={Printer}/>
                        </IconButton>
                    </Tooltip>
                }
            </Row>

            <div className={'w-100 my-3'}/>

            <div className={'d-flex flex-wrap w-100 justify-content-start'}>
                <Col xs={12} md={6} xl={4} className={'mb-3'}>
                    <SummaryInfoCard
                        header={documentInfoSummary.header}
                        body={documentInfoSummary.body}
                        loading={loading}
                        loadingCount={documentInfoSummary.body.length}
                    />
                </Col>
                <Col xs={12} md={6} xl={4} className={'mb-3'}>
                    <PurchaseVendorInfoCard
                        vendorId={vendorId}
                        loading={loading}
                    />
                </Col>
                <Col xs={12} md={6} xl={4} className={'mb-3'} style={{overflowY: 'hidden'}}>
                    <SummaryActionCard
                        className={'auto-height'}
                        header={billInfoSummary.header}
                        body={billInfoSummary.body}
                        actions={[]}
                        loading={loading}
                        loadingCount={billInfoSummary.body.length}
                    />
                </Col>
            </div>

            <div className={'w-100 my-2'}/>

            <div className={'w-100 flex-grow-1'}>
                <Tabs value={tab} onChange={onTabChange}>
                    {
                        tabsList.map((entry) => (
                            <Tab
                                key={entry.value}
                                label={entry.label}
                                value={entry.value}
                            />
                        ))
                    }
                </Tabs>
            </div>

            <div className={'my-3 w-100'}/>

            {
                tab === tabs.products &&
                <PurchaseOrderSingleProductsSection
                    items={order?.items ?? []}
                    loading={loading}
                    updating={updating}
                    editable={canManageOrderDetails}
                    onUpdateOrderQty={onUpdateOrderQty}
                    onUpdateCost={onUpdateCost}
                    onAddSerial={onAddSerial}
                    onRemoveSerial={onRemoveSerial}
                />
            }
            {
                tab === tabs.addresses &&
                <OrderInformationViewReadOnlyAddressesSection
                    billing={order?.billingAddress}
                    shipping={order?.shippingAddress}
                    loading={loading}
                />
            }
            {
                tab === tabs.logs &&
                <PurchaseOrderSingleLogsSection
                    logs={order?.logs ?? []}
                    loading={loading}
                />
            }

            <UpdatePurchaseOrderPropertiesDialog
                open={!!propertiesDialog.open}
                close={propertiesDialog.close ?? (() => setPropertiesDialog(initUpdatePurchaseOrderPropertiesDialog))}
                entityToShow={propertiesDialog.entityToShow}
                warehouse={propertiesDialog.warehouse}
                update={propertiesDialog.update}
            />

            <div className={'my-3 w-100'}/>
        </div>
    );
};

export default PurchaseOrderSingleView;
